import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Spinner, Modal, Button } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch } from "react-icons/fa";
import { fetchAdminBlogs, deleteBlog } from "../utils/blogApi";
import { toast } from "react-toastify";

export default function BlogList() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteModal, setDeleteModal] = useState({ show: false, blog: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      const data = await fetchAdminBlogs();
      if (data.success) {
        setBlogs(data.blogs);
      }
    } catch (err) {
      console.error("Error loading blogs:", err);
      toast.error("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.blog) return;
    try {
      setDeleting(true);
      await deleteBlog(deleteModal.blog.id);
      toast.success("Blog deleted successfully");
      setBlogs((prev) => prev.filter((b) => b.id !== deleteModal.blog.id));
      setDeleteModal({ show: false, blog: null });
    } catch (err) {
      console.error("Error deleting blog:", err);
      toast.error("Failed to delete blog");
    } finally {
      setDeleting(false);
    }
  };

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch = blog.title
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || blog.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="blog-admin">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="page-title">Blog Posts</h1>
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={() => navigate("/dashboard/blog/create")}
        >
          <FaPlus /> New Post
        </button>
      </div>

      {/* Filters */}
      <div className="blog-admin-filters mb-4">
        <div className="d-flex gap-3 flex-wrap">
          <div className="position-relative flex-grow-1" style={{ maxWidth: "400px" }}>
            <FaSearch
              className="position-absolute"
              style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#999" }}
            />
            <input
              type="text"
              className="form-control"
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: "36px" }}
            />
          </div>
          <select
            className="form-select"
            style={{ width: "180px" }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Blog Table */}
      {filteredBlogs.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th style={{ width: "80px" }}>Cover</th>
                <th>Title</th>
                <th>Status</th>
                <th>Type</th>
                <th>Views</th>
                <th>Date</th>
                <th style={{ width: "140px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlogs.map((blog) => (
                <tr key={blog.id}>
                  <td>
                    {blog.coverImage ? (
                      <img
                        src={blog.coverImage}
                        alt=""
                        style={{
                          width: "60px",
                          height: "40px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "60px",
                          height: "40px",
                          backgroundColor: "#e9ecef",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          color: "#999",
                        }}
                      >
                        No img
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="fw-semibold">{blog.title}</div>
                    <small className="text-muted">{blog.readingTime}</small>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        blog.status === "published"
                          ? "bg-success"
                          : "bg-warning text-dark"
                      }`}
                    >
                      {blog.status}
                    </span>
                  </td>
                  <td>
                    <small>{blog.type || "—"}</small>
                  </td>
                  <td>{blog.views || 0}</td>
                  <td>
                    <small>{formatDate(blog.publishedAt || blog.createdAt)}</small>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {blog.status === "published" && (
                        <Link
                          to={`/blog/${blog.slug}`}
                          target="_blank"
                          className="btn btn-sm btn-outline-secondary"
                          title="View"
                        >
                          <FaEye />
                        </Link>
                      )}
                      <button
                        className="btn btn-sm btn-outline-primary"
                        title="Edit"
                        onClick={() =>
                          navigate(`/dashboard/blog/edit/${blog.id}`)
                        }
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        title="Delete"
                        onClick={() =>
                          setDeleteModal({ show: true, blog })
                        }
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">
            {searchTerm || filterStatus !== "all"
              ? "No blogs match your filters."
              : "No blog posts yet. Create your first one!"}
          </p>
          {!searchTerm && filterStatus === "all" && (
            <button
              className="btn btn-primary"
              onClick={() => navigate("/dashboard/blog/create")}
            >
              <FaPlus /> Create First Blog
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteModal.show}
        onHide={() => setDeleteModal({ show: false, blog: null })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Blog Post</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>"{deleteModal.blog?.title}"</strong>? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDeleteModal({ show: false, blog: null })}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
